import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { EMPTY, merge, Observable, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { StateMemento } from 'wflow-cdk';
import { getOrg } from 'wflow-main-app/src/app/shared/utils/route.utils';
import {
  PeppolConnection,
  PeppolConnectionStatus,
  PeppolEnrollmentState,
} from '../interfaces/peppol-enrollment.interface';
import { PEPPOL_CONNECTION_API } from '../tokens/peppol-connection-api.token';

@Injectable({ providedIn: 'root' })
export class PeppolEnrollmentService {
  private readonly connection = inject(PEPPOL_CONNECTION_API);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly state = signal<PeppolEnrollmentState>('loading');
  private readonly reloads = new Subject<string>();

  readonly enrollmentState = this.state.asReadonly();

  readonly isEnrolled = computed(() => this.state() === 'enrolled');

  readonly resolved$ = toObservable(this.state).pipe(
    filter((state) => state !== 'loading'),
    take(1),
  );

  constructor() {
    this.watchOrganization();
  }

  reload(): void {
    const organization = getOrg();
    if (!organization) {
      return;
    }
    this.state.set('loading');
    this.reloads.next(organization);
  }

  // Every organization has its own cached answer and its own status check —
  // switching the org re-seeds the state from that org's cache and revalidates.
  private watchOrganization(): void {
    const organizations$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => getOrg()),
      startWith(getOrg()),
      filter((organization) => !!organization),
      distinctUntilChanged(),
      tap((organization) =>
        this.state.set(
          this.initialState(this.cacheFor(organization).getState()),
        ),
      ),
    );

    // `switchMap` cancels the check in flight, so a slow earlier response
    // cannot land after a newer one and overwrite it.
    merge(organizations$, this.reloads)
      .pipe(
        switchMap((organization) => this.requestStatus(organization)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private requestStatus(organization: string): Observable<PeppolConnection> {
    return this.connection.getConnection(organization).pipe(
      tap(({ status }) => {
        const enrolled = status === PeppolConnectionStatus.Active;
        this.cacheFor(organization).change(enrolled);
        this.state.set(enrolled ? 'enrolled' : 'unenrolled');
      }),
      // A failed check keeps the last known answer; only an unresolved
      // first visit surfaces as 'error' (retry card).
      catchError(() => {
        if (this.state() === 'loading') {
          this.state.set('error');
        }
        return EMPTY;
      }),
    );
  }

  private cacheFor(organization: string): StateMemento<boolean> {
    return new StateMemento<boolean>({
      key: `PEPPOL_ENROLLED_${organization}`,
      storage: localStorage,
    });
  }

  private initialState(
    cached: boolean | null | undefined,
  ): PeppolEnrollmentState {
    if (cached == null) {
      return 'loading';
    }
    return cached ? 'enrolled' : 'unenrolled';
  }
}
